import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Trash2, Save, Eye, EyeOff, MessageSquare, Megaphone, BookOpen, ArrowLeft, Image, Pencil, Upload, Send, Calendar } from 'lucide-react';
import api from '../api';

const API = process.env.REACT_APP_BACKEND_URL || '';

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
}

async function cropImageTo16x9(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = objectUrl;
    });

    const targetRatio = 16 / 9;
    const sourceRatio = img.width / img.height;
    let sx = 0;
    let sy = 0;
    let sw = img.width;
    let sh = img.height;

    if (sourceRatio > targetRatio) {
      sw = Math.round(img.height * targetRatio);
      sx = Math.round((img.width - sw) / 2);
    } else if (sourceRatio < targetRatio) {
      sh = Math.round(img.width / targetRatio);
      sy = Math.round((img.height - sh) / 2);
    }

    const canvas = document.createElement('canvas');
    canvas.width = 1600;
    canvas.height = 900;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Image upload helper component
function ImageUploader({ value, onChange, label = "Image" }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    const processedFile = await cropImageTo16x9(file);
    formData.append('file', processedFile);
    try {
      const { data } = await api.post(`/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (err) {
      alert('Upload failed. Try again.');
    }
    setUploading(false);
  };

  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}
        <span className="text-gray-600 ml-1">(Auto-cropped to 16:9, 1200x675px or similar)</span>
      </label>
      <div className="flex gap-2">
        <input value={value} onChange={e => onChange(e.target.value)} placeholder="Paste URL or upload below"
          className="flex-1 px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all text-sm" />
        <label className={`flex items-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-neonBlue hover:border-neonBlue/30 cursor-pointer transition-all text-sm ${uploading ? 'opacity-50' : ''}`}>
          <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={uploading} />
        </label>
      </div>
      {value && (
        <div className="mt-2 h-24 rounded-lg overflow-hidden bg-gray-900 w-48">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState('stories');
  const [stories, setStories] = useState([]);
  const [peeks, setPeeks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [banners, setBanners] = useState([]);
  const [telegramChannels, setTelegramChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const h = getAuthHeaders();
      const [s, p, ct, a, b, t] = await Promise.all([
        api.get(`/api/admin/stories`),
        api.get(`/api/admin/sneak-peeks`),
        api.get(`/api/admin/contacts`),
        api.get(`/api/admin/announcements`),
        api.get(`/api/admin/banners`),
        api.get(`/api/admin/telegram-channels`),
      ]);
      setStories(s.data); setPeeks(p.data); setContacts(ct.data);
      setAnnouncements(a.data); setBanners(b.data); setTelegramChannels(t.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('admin_token'); navigate('/admin');
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchAll();
  }, [fetchAll, navigate]);

  const handleLogout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); };

  if (loading) return <div className="min-h-screen bg-darkBg flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-neonBlue/30 border-t-neonBlue rounded-full" /></div>;

  const tabs = [
    { id: 'stories', label: 'Stories', icon: BookOpen },
    { id: 'banners', label: 'Banners', icon: Image },
    { id: 'peeks', label: 'Sneak Peeks', icon: Eye },
    { id: 'telegram', label: 'Telegram', icon: Send },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'contacts', label: 'Messages', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-darkBg" data-testid="admin-dashboard">
      <div className="bg-cardBg border-b border-white/5 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-gray-500 hover:text-neonBlue transition-colors" data-testid="admin-home-link"><ArrowLeft size={20} /></a>
            <h1 className="font-heading text-xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-gray-400 hover:text-red-400 text-sm transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            return <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${tab === t.id ? 'bg-neonBlue text-black' : 'bg-cardBg text-gray-400 border border-white/5 hover:border-neonBlue/30 hover:text-white'}`}>
              <Icon size={14} /> {t.label}
            </button>;
          })}
        </div>
        {tab === 'stories' && <StoriesTab stories={stories} onRefresh={fetchAll} />}
        {tab === 'banners' && <BannersTab banners={banners} onRefresh={fetchAll} />}
        {tab === 'peeks' && <SneakPeeksTab peeks={peeks} stories={stories} onRefresh={fetchAll} />}
        {tab === 'telegram' && <TelegramTab channels={telegramChannels} onRefresh={fetchAll} />}
        {tab === 'announcements' && <AnnouncementsTab announcements={announcements} onRefresh={fetchAll} />}
        {tab === 'contacts' && <ContactsTab contacts={contacts} />}
      </div>
    </div>
  );
}

// Stories Tab
function StoriesTab({ stories, onRefresh }) {
  const empty = { story_name: '', description: '', image_url: '', total_episodes: 0, target_episodes: 2000, daily_release: 0, status: 'Ongoing', telegram_link: '', is_active: true };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.story_name.trim()) return;
    setSaving(true);
    const h = getAuthHeaders();
    if (editId) await api.put(`/api/admin/stories/${editId}`, form);
    else await api.post(`/api/admin/stories`, form);
    setForm(empty); setEditId(null); setSaving(false); onRefresh();
  };

  const startEdit = (s) => { setEditId(s.id); setForm({ story_name: s.story_name, description: s.description, image_url: s.image_url, total_episodes: s.total_episodes, target_episodes: s.target_episodes, daily_release: s.daily_release, status: s.status, telegram_link: s.telegram_link, is_active: s.is_active }); };
  const del = async (id) => { await api.delete(`/api/admin/stories/${id}`); onRefresh(); };

  return (
    <div data-testid="admin-stories-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">{editId ? 'Edit Story' : 'Add New Story'}</h2>
      <div className="glass-card rounded-xl p-6 mb-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.story_name} onChange={e => setForm({...form, story_name: e.target.value})} data-testid="admin-story-name" placeholder="Story name"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="admin-story-status"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all">
            <option value="Ongoing">Ongoing</option>
            <option value="New">New</option>
            <option value="Completed">Completed</option>
            <option value="Paused">Paused</option>
            <option value="Coming Soon">Coming Soon</option>
            <option value="Dropped">Dropped</option>
          </select>
        </div>
        <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="admin-story-desc" placeholder="Story description" rows={2}
          className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all resize-none" />
        <ImageUploader value={form.image_url} onChange={url => setForm({...form, image_url: url})} label="Story Image" />
        <input value={form.telegram_link} onChange={e => setForm({...form, telegram_link: e.target.value})} placeholder="Telegram link"
          className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        <div className="grid grid-cols-3 gap-4">
          <div><label className="block text-xs text-gray-500 mb-1">Total Episodes</label>
            <input type="number" value={form.total_episodes} onChange={e => setForm({...form, total_episodes: parseInt(e.target.value)||0})} data-testid="admin-story-total"
              className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Target Episodes</label>
            <input type="number" value={form.target_episodes} onChange={e => setForm({...form, target_episodes: parseInt(e.target.value)||0})}
              className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Daily Release</label>
            <input type="number" value={form.daily_release} onChange={e => setForm({...form, daily_release: parseInt(e.target.value)||0})}
              className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all" /></div>
        </div>
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="admin-save-story-btn"
            className="flex items-center gap-2 bg-neonBlue text-black font-bold px-6 py-2.5 rounded-full hover:bg-neonBlue/80 disabled:opacity-50 transition-all text-sm">
            {editId ? <><Save size={14} /> Update Story</> : <><Plus size={14} /> Add Story</>}
          </button>
          {editId && <button onClick={() => { setEditId(null); setForm(empty); }} className="px-6 py-2.5 rounded-full text-gray-400 border border-white/10 text-sm hover:text-white transition-all">Cancel</button>}
        </div>
      </div>
      <h3 className="font-heading text-lg font-semibold text-white mb-4">All Stories ({stories.length})</h3>
      <div className="space-y-3">
        {stories.map((s, i) => (
          <div key={s.id} data-testid={`admin-story-item-${i}`} className="glass-card rounded-xl p-4 flex items-center gap-4">
            {s.image_url && <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-900 shrink-0"><img src={s.image_url} alt={s.story_name} className="w-full h-full object-cover" /></div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><span className="font-heading font-semibold text-white text-sm">{s.story_name}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>{s.status}</span></div>
              <p className="text-gray-500 text-xs">{s.total_episodes} / {s.target_episodes} eps | {s.daily_release}/day</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => startEdit(s)} data-testid={`admin-edit-story-${i}`} className="p-2 text-gray-500 hover:text-neonBlue transition-colors"><Pencil size={16} /></button>
              <button onClick={() => del(s.id)} data-testid={`admin-delete-story-${i}`} className="p-2 text-gray-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sneak Peeks Tab
function SneakPeeksTab({ peeks, stories, onRefresh }) {
  const [storyName, setStoryName] = useState(stories[0]?.story_name || '');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !text.trim() || !storyName) return;
    setSaving(true);
    const formattedDate = new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const payload = { story_name: storyName, title, text, date: formattedDate, is_active: true };
    const h = getAuthHeaders();
    if (editId) await api.put(`/api/admin/sneak-peeks/${editId}`, payload);
    else await api.post(`/api/admin/sneak-peeks`, payload);
    setTitle(''); setText(''); setEditId(null);
    setSaving(false); onRefresh();
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setStoryName(p.story_name);
    setTitle(p.title);
    setText(p.text);
  };

  const toggle = async (p) => {
    await api.put(`/api/admin/sneak-peeks/${p.id}`, { story_name: p.story_name, title: p.title, text: p.text, date: p.date, is_active: !p.is_active });
    onRefresh();
  };

  const del = async (id) => { await api.delete(`/api/admin/sneak-peeks/${id}`); onRefresh(); };

  return (
    <div data-testid="admin-peeks-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">{editId ? 'Edit Sneak Peek' : 'Add New Sneak Peek'}</h2>
      <div className="glass-card rounded-xl p-6 mb-6 space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <select value={storyName} onChange={e => setStoryName(e.target.value)} data-testid="admin-peek-story-select"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all">
            {stories.map(s => <option key={s.id} value={s.story_name}>{s.story_name}</option>)}
          </select>
          <input value={title} onChange={e => setTitle(e.target.value)} data-testid="admin-peek-title-input"
            placeholder="Episode title (e.g., Episode 86-90 or Episode 91)"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="admin-peek-date-input"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all" />
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} data-testid="admin-peek-text-input" placeholder="Sneak peek text in Hindi..." rows={3}
          className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all resize-none" />
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="admin-add-peek-btn"
            className="flex items-center gap-2 bg-neonBlue text-black font-bold px-6 py-2.5 rounded-full hover:bg-neonBlue/80 disabled:opacity-50 transition-all text-sm">
            {editId ? <><Save size={14} /> Update Sneak Peek</> : <><Plus size={14} /> Add Sneak Peek</>}
          </button>
          {editId && <button onClick={() => { setEditId(null); setTitle(''); setText(''); }}
            className="px-6 py-2.5 rounded-full text-gray-400 border border-white/10 text-sm hover:text-white transition-all">Cancel</button>}
        </div>
      </div>
      <div className="space-y-3">
        {peeks.map((p, i) => (
          <div key={p.id} data-testid={`admin-peek-item-${i}`} className="glass-card rounded-xl p-5 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="bg-neonBlue/10 text-neonBlue text-[10px] font-bold px-2 py-0.5 rounded-full">{p.story_name}</span>
                <span className="font-heading font-semibold text-white text-sm">{p.title}</span>
                {p.date && <span className="flex items-center gap-1 text-[10px] text-gray-500"><Calendar size={8} />{p.date}</span>}
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>{p.is_active ? 'Active' : 'Hidden'}</span>
              </div>
              <p className="font-hindi text-gray-400 text-sm truncate">{p.text}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => startEdit(p)} data-testid={`admin-edit-peek-${i}`} className="p-2 text-gray-500 hover:text-neonBlue transition-colors"><Pencil size={16} /></button>
              <button onClick={() => toggle(p)} data-testid={`admin-toggle-peek-${i}`} className="p-2 text-gray-500 hover:text-neonBlue transition-colors">{p.is_active ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => del(p.id)} data-testid={`admin-delete-peek-${i}`} className="p-2 text-gray-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Banners Tab
function BannersTab({ banners, onRefresh }) {
  const emptyForm = { title: '', subtitle: '', image_url: '', cta_text: 'Join Telegram', cta_link: 'https://t.me/the_warrior_SunyaGatha' };
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.image_url.trim()) return;
    setSaving(true);
    const h = getAuthHeaders();
    if (editId) await api.put(`/api/admin/banners/${editId}`, { ...form, is_active: true });
    else await api.post(`/api/admin/banners`, { ...form, is_active: true });
    setForm(emptyForm); setEditId(null);
    setSaving(false); onRefresh();
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setForm({ title: b.title, subtitle: b.subtitle, image_url: b.image_url, cta_text: b.cta_text, cta_link: b.cta_link });
  };

  const del = async (id) => { await api.delete(`/api/admin/banners/${id}`); onRefresh(); };

  return (
    <div data-testid="admin-banners-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">{editId ? 'Edit Banner' : 'Add New Banner'}</h2>
      <div className="glass-card rounded-xl p-6 mb-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="admin-banner-title" placeholder="Banner title"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <input value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} placeholder="Subtitle"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        </div>
        <ImageUploader value={form.image_url} onChange={url => setForm({...form, image_url: url})} label="Banner Image" />
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.cta_text} onChange={e => setForm({...form, cta_text: e.target.value})} placeholder="Button text"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <input value={form.cta_link} onChange={e => setForm({...form, cta_link: e.target.value})} placeholder="Button link"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        </div>
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="admin-add-banner-btn"
            className="flex items-center gap-2 bg-neonBlue text-black font-bold px-6 py-2.5 rounded-full hover:bg-neonBlue/80 disabled:opacity-50 transition-all text-sm">
            {editId ? <><Save size={14} /> Update Banner</> : <><Plus size={14} /> Add Banner</>}
          </button>
          {editId && <button onClick={() => { setEditId(null); setForm(emptyForm); }}
            className="px-6 py-2.5 rounded-full text-gray-400 border border-white/10 text-sm hover:text-white transition-all">Cancel</button>}
        </div>
      </div>
      <div className="space-y-3">
        {banners.map((b, i) => (
          <div key={b.id} data-testid={`admin-banner-item-${i}`} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-900 shrink-0"><img src={b.image_url} alt={b.title} className="w-full h-full object-cover" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-white text-sm">{b.title}</div>
              <p className="text-gray-500 text-xs truncate">{b.subtitle}</p>
              <p className="text-neonBlue text-[10px] mt-0.5">{b.cta_text} → {b.cta_link}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => startEdit(b)} data-testid={`admin-edit-banner-${i}`} className="p-2 text-gray-500 hover:text-neonBlue transition-colors"><Pencil size={16} /></button>
              <button onClick={() => del(b.id)} data-testid={`admin-delete-banner-${i}`} className="p-2 text-gray-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Telegram Tab
function TelegramTab({ channels, onRefresh }) {
  const [form, setForm] = useState({ name: '', description: '', hindi_text: '', url: '', channel_type: 'channel', is_active: true });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) return;
    setSaving(true);
    const h = getAuthHeaders();
    if (editId) await api.put(`/api/admin/telegram-channels/${editId}`, form);
    else await api.post(`/api/admin/telegram-channels`, form);
    setForm({ name: '', description: '', hindi_text: '', url: '', channel_type: 'channel', is_active: true });
    setEditId(null); setSaving(false); onRefresh();
  };

  const startEdit = (ch) => { setEditId(ch.id); setForm({ name: ch.name, description: ch.description, hindi_text: ch.hindi_text, url: ch.url, channel_type: ch.channel_type, is_active: ch.is_active }); };
  const del = async (id) => { await api.delete(`/api/admin/telegram-channels/${id}`); onRefresh(); };

  return (
    <div data-testid="admin-telegram-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">{editId ? 'Edit Channel' : 'Add Telegram Channel'}</h2>
      <div className="glass-card rounded-xl p-6 mb-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="admin-tg-name" placeholder="Channel name"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <input value={form.url} onChange={e => setForm({...form, url: e.target.value})} data-testid="admin-tg-url" placeholder="https://t.me/..."
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description (English)"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
          <input value={form.hindi_text} onChange={e => setForm({...form, hindi_text: e.target.value})} placeholder="Description (Hindi)"
            className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        </div>
        <select value={form.channel_type} onChange={e => setForm({...form, channel_type: e.target.value})}
          className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white focus:border-neonBlue outline-none transition-all">
          <option value="main">Main Channel</option><option value="updates">Updates</option><option value="buy">Buy/Contact</option><option value="channel">Other</option>
        </select>
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="admin-save-tg-btn"
            className="flex items-center gap-2 bg-neonBlue text-black font-bold px-6 py-2.5 rounded-full hover:bg-neonBlue/80 disabled:opacity-50 transition-all text-sm">
            {editId ? <><Save size={14} /> Update</> : <><Plus size={14} /> Add Channel</>}
          </button>
          {editId && <button onClick={() => { setEditId(null); setForm({ name: '', description: '', hindi_text: '', url: '', channel_type: 'channel', is_active: true }); }}
            className="px-6 py-2.5 rounded-full text-gray-400 border border-white/10 text-sm hover:text-white transition-all">Cancel</button>}
        </div>
      </div>
      <div className="space-y-3">
        {channels.map((ch, i) => (
          <div key={ch.id} data-testid={`admin-tg-item-${i}`} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><span className="font-heading font-semibold text-white text-sm">{ch.name}</span>
                <span className="text-[10px] text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{ch.channel_type}</span></div>
              <p className="text-neonBlue text-xs">{ch.url}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => startEdit(ch)} className="p-2 text-gray-500 hover:text-neonBlue transition-colors"><Pencil size={16} /></button>
              <button onClick={() => del(ch.id)} data-testid={`admin-delete-tg-${i}`} className="p-2 text-gray-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Announcements Tab
function AnnouncementsTab({ announcements, onRefresh }) {
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);
  const add = async () => { if (!newText.trim()) return; setSaving(true); await api.post(`/api/admin/announcements`, { text: newText, is_active: true }); setNewText(''); setSaving(false); onRefresh(); };
  const del = async (id) => { await api.delete(`/api/admin/announcements/${id}`); onRefresh(); };
  return (
    <div data-testid="admin-announcements-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">Manage Announcements</h2>
      <div className="glass-card rounded-xl p-6 mb-6 space-y-4">
        <input value={newText} onChange={e => setNewText(e.target.value)} data-testid="admin-announcement-input" placeholder="New announcement text..."
          className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue outline-none transition-all" />
        <button onClick={add} disabled={saving} data-testid="admin-add-announcement-btn"
          className="flex items-center gap-2 bg-neonBlue text-black font-bold px-6 py-2.5 rounded-full hover:bg-neonBlue/80 disabled:opacity-50 transition-all text-sm"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-3">{announcements.map((a, i) => (
        <div key={a.id} data-testid={`admin-announcement-item-${i}`} className="glass-card rounded-xl p-4 flex items-center justify-between">
          <span className="text-gray-300 text-sm">{a.text}</span>
          <button onClick={() => del(a.id)} className="p-2 text-gray-500 hover:text-red-400 transition-colors shrink-0"><Trash2 size={16} /></button>
        </div>
      ))}</div>
    </div>
  );
}

// Contacts Tab
function ContactsTab({ contacts }) {
  return (
    <div data-testid="admin-contacts-tab">
      <h2 className="font-heading text-2xl font-bold text-white mb-6">Messages ({contacts.length})</h2>
      {contacts.length === 0 ? <div className="glass-card rounded-xl p-8 text-center text-gray-500">No messages yet</div> :
        <div className="space-y-3">{contacts.map((c, i) => (
          <div key={c.id} data-testid={`admin-contact-item-${i}`} className="glass-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-2 gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-semibold text-white text-sm">{c.name}</span>
                {c.telegram_username && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#2AABEE]/10 text-[#2AABEE] border border-[#2AABEE]/20">{c.telegram_username}</span>}
              </div>
              <span className="text-xs text-gray-600">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span></div>
            <p className="text-gray-400 text-sm">{c.message}</p>
          </div>
        ))}</div>
      }
    </div>
  );
}
